import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Card, IconButton, Typography } from '@totalsoft/rocket-ui';
import { Tooltip } from '@mui/material';
import {
  FinancialProductRuleCardContent,
  TypographyPaddingRight5,
  TypographyWordWrap,
  TypographyMax4Rows,
  GridFullSize,
  GridFullSizePadding5
} from './FinancialProductRuleListStyle';

const FinancialProductRuleListItem = ({ t, item, index, onEditLink, onDeleteLink, canWrite }) => {
  
  const filterCondition = useMemo(() => {

    return item?.filterCondition
      ?.trim()
      .split(' ')
      .reduce((arr, itemValue) => {
        if (itemValue.length > 1 && (itemValue.indexOf('(') > -1 || itemValue.indexOf(')') > -1)) {
          const op = itemValue.indexOf('(') > -1 ? '(' : ')';
          if (itemValue.indexOf('(') > -1) arr.push({ value: op, isOperator: true, isBracket: true });
          arr.push({ value: itemValue.replace(op, ''), isOperator: false });
          if (itemValue.indexOf(')') > -1) arr.push({ value: op, isOperator: true, isBracket: true });
        } else {
          const isOperator = !parseInt(itemValue);
          arr.push({ value: itemValue, isOperator, isBracket: false });
        }
        return arr;
    }, []);

  }, [item?.filterCondition]);

  const contentGridFilterCondition = useMemo(() => {
  
    return filterCondition?.map(f => {
      if (f.isOperator) {
        return (
          <>
            <Typography fontWeight='bold' component='span'>
              <>
                {' ' + f.value}
                {f.isBracket ? <></> : <br />}
              </>
            </Typography>
          </>
        );
      } else {
        const n = item.ruleFilters?.find(element => element.orderNo === parseInt(f.value));
        if (n) {
          return (
            <>
              <Typography fontWeight='bold' component='span'>
                {n.paramName}{' '}
              </Typography>
              <Typography component='span'>
                {n.operatorName}{' '}
                {!n.operatorIsMultipleSelection
                  ? Number.parseFloat(n.filterValue)
                    ? n.filterValue
                    : '"' + n.filterValue + '"'
                  : '(' +
                    n.filterValues
                      .map(n => {
                        return Number.parseFloat(n.filterValue) ? n.filterValue : '"' + n.filterValue + '"';
                      })
                      .join(', ') +
                    ')'}
              </Typography>
            </>
          );
        }
      }
      return <></>;
    });

  }, [filterCondition, item.ruleFilters]);

  const contentGridOutputDataDetailsContainer = useMemo(() => { 

      return item.outputData.map(n => {
        return (
        <>
          <Typography fontWeight='bold' component='span'>
            {n.paramName}
          </Typography>
          <Typography component='span'>
            {' = '}
            {n.defaultValue}
            {n.minValue === n.maxValue ? '' : ' (min: ' + n.minValue + ', max: ' + n.maxValue + ')'}
            <br />
          </Typography>
        </>
        );
      });

  }, [item.outputData]);

  const contentGridOutputDictionariesDetailsContainer = useMemo(() => {

    return item.outputDictionaries.map(n => {
      return (
        <>
          <Typography fontWeight='bold' component='span'>
            {n.paramName}
          </Typography>
          <Typography component='span'>
            {' = '}
            {Number.parseFloat(n.filterValue) ? n.filterValue : '"' + n.filterValue + '"'}
            <br />
          </Typography>
        </>
      );
    });

  }, [item.outputDictionaries]);

  const contentGridRuleContainer = useMemo(() => {
    return (<span>
        <Typography fontWeight='bold' component='span' color='secondary'>
          {item.generalYN === true ? (
            <>
              {t('FinancialProduct.Details.Rules.GeneralYN')}
              <br />
            </>
          ) : (
            <></>
          )}
        </Typography>
        <TypographyPaddingRight5 fontWeight='bold' component='span'>
          {t('FinancialProduct.Details.Rules.Code')}:
        </TypographyPaddingRight5>
        <Typography component='span'>
          {item.ruleCode}
          <br />
        </Typography>
        <TypographyPaddingRight5 fontWeight='bold' component='span'>
          {t('FinancialProduct.Details.Rules.Description')}:
        </TypographyPaddingRight5>
        <TypographyWordWrap component='span'>{item.ruleDescription}</TypographyWordWrap>
      </span>);

  }, [item.generalYN, item.ruleCode, item.ruleDescription, t]);

  return (
    <Card disablePadding spacing={2}>
      <FinancialProductRuleCardContent container colors={item} height='8vw'>
        <Grid item xs={3} id={'ruleList_financialProductRule_' + index + '_filter'} height='100%'>
          <Grid item xs={12} id={'ruleList_financialProductRule_' + index + '_filterConditionTitle'}>
            <strong> {t('FinancialProduct.Details.Rules.Filters.Condition')}</strong>:
          </Grid>
          <GridFullSizePadding5 item xs={12} layout={'row'} id={'ruleList_financialProductRule_' + index + '_filterCondition'}>
            <TypographyMax4Rows>
              <Tooltip placement='top' title={contentGridFilterCondition ?? <></>}>
                {contentGridFilterCondition ?? <></>}
              </Tooltip>
            </TypographyMax4Rows>
          </GridFullSizePadding5>
        </Grid>
        <Grid item xs={3} id={'ruleList_financialProductRule_' + index + '_outputData'} height='100%'>
          <Grid item xs={12} id={'ruleList_financialProductRule_' + index + '_outputDataTitle'}>
            <strong> {t('FinancialProduct.Details.Rules.Details.OutputData.Title')}</strong>:
          </Grid>
          <GridFullSizePadding5 item xs={12} id={'ruleList_financialProductRule_' + index + '_outputDataDetailsContainer'}>
            <TypographyMax4Rows>
              <Tooltip placement='top' title={contentGridOutputDataDetailsContainer ?? <></>}>
                {contentGridOutputDataDetailsContainer ?? <></>}
              </Tooltip>
            </TypographyMax4Rows>
          </GridFullSizePadding5>
        </Grid>
        <Grid item xs={3} id={'ruleList_financialProductRule_' + index + '_outputDictionaries'} height='100%'>
          <Grid item xs={12} id={'ruleList_financialProductRule_' + index + '_outputDictionariesTitle'}>
            <strong> {t('FinancialProduct.Details.Rules.Details.OutputDictionaries.Title')}</strong>:
          </Grid>
          <GridFullSizePadding5 item xs={12} id={'ruleList_financialProductRule_' + index + '_outputDictionariesDetailsContainer'}>
            <TypographyMax4Rows>
              <Tooltip placement='top' title={contentGridOutputDictionariesDetailsContainer ?? <></>}>
                {contentGridOutputDictionariesDetailsContainer ?? <></>}
              </Tooltip>
            </TypographyMax4Rows>
          </GridFullSizePadding5>
        </Grid>
        <GridFullSize item xs={2} id={'ruleList_financialProductRule_' + index + '_Details'}>
          <TypographyMax4Rows>
            <Tooltip placement='top' title={contentGridRuleContainer ?? <></>}>
              {contentGridRuleContainer ?? <></>}
            </Tooltip>
          </TypographyMax4Rows>
        </GridFullSize>
        <Grid item xs={1} height='100%'>
          <Grid
            item
            xs={12}
            height='100%'
            id={'ruleList_financialProductRule_' + index + '_commandColumn'}
            display='flex'
            flexDirection='row'
            justifyContent='flex-end'
            alignItems='flex-end'
          >
            <IconButton
              type='edit'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.Edit')}
              onClick={onEditLink(item.financialProductRuleId)}
            />
            <IconButton
              type='delete'
              fontSize='medium'
              color='transparent'
              tooltip={t('FinancialProduct.Delete')}
              onClick={onDeleteLink(item.financialProductRuleId)}
              disabled={!canWrite}
            />
          </Grid>
        </Grid>
      </FinancialProductRuleCardContent>
    </Card>
  );
};

FinancialProductRuleListItem.propTypes = {
  t: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onEditLink: PropTypes.func.isRequired,
  onDeleteLink: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired
};

export default withTranslation()(FinancialProductRuleListItem);
